import PropTypes from "prop-types";
import { memo, useState } from "react";
import { Responsive, WidthProvider } from "react-grid-layout/legacy";
import { BiCopy } from "react-icons/bi";
import { FiEdit } from "react-icons/fi";
import { MdDeleteForever } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import { v4 as uuid } from "uuid";

// Chart Components
import ChartFetch from "@/components/HomeCharts/ChartFetch";
import DashboardImage from "@/components/LayoutCharts/DashboardImage";
import DialFetch from "@/components/LayoutCharts/DialFetch";
import LayoutAlarmNotification from "@/components/LayoutCharts/LayoutAlarmNotification";
import LayoutTextBox from "@/components/LayoutCharts/LayoutTextBox";
import TableChart from "@/components/LayoutCharts/TableChart";
import { setDashboardFilter, setPrevTabId } from "@/redux/slices/dashboardV2.slice";
import { cleanObject } from "@/utils/globalUtils";
import { FaExternalLinkAlt } from "react-icons/fa";

const ResponsiveReactGridLayout = WidthProvider(Responsive);

// Component Registry for cleaner rendering
const COMPONENT_MAP = {
  // chart fetch
  bar: ChartFetch,
  line: ChartFetch,
  pie: ChartFetch,
  comboBar: ChartFetch,

  // dial fetch
  dial: DialFetch,
  splitCounter: DialFetch,
  tank: DialFetch,
  dynamicBox: DialFetch,
  loadSummary: DialFetch,
  thermometer: DialFetch,
  counter: DialFetch,

  // no extra api
  textBox: LayoutTextBox,
  html: LayoutTextBox,
  image: DashboardImage,
  alarmNotification: LayoutAlarmNotification,

  // others
  table: TableChart,
};

const GRID_CONFIG = {
  breakpoints: { lg: 900, md: 700, sm: 500, xs: 400, xxs: 300 },
  cols: { lg: 16, md: 12, sm: 8, xs: 4, xxs: 2 },
};

const calculateNextPosition = (layout = [], itemWidth = 3, itemHeight = 3, maxCols = 16) => {
  let maxY = 0;
  const occupied = new Set();

  layout.forEach((item) => {
    for (let i = 0; i < item.w; i++) {
      for (let j = 0; j < item.h; j++) {
        occupied.add(`${item.x + i},${item.y + j}`);
      }
    }
    maxY = Math.max(maxY, item.y + item.h);
  });

  for (let y = 0; y <= maxY + 1; y++) {
    for (let x = 0; x <= maxCols - itemWidth; x++) {
      let fits = true;
      for (let i = 0; i < itemWidth; i++) {
        for (let j = 0; j < itemHeight; j++) {
          if (occupied.has(`${x + i},${y + j}`)) {
            fits = false;
            break;
          }
        }
        if (!fits) break;
      }
      if (fits) return { x, y };
    }
  }
  return { x: 0, y: maxY };
};

const DashboardLayout = memo(
  ({
    gridData = [],
    setGridData,
    setLayouts,
    layouts = {},
    mode = "view",
    setEditItem = () => {},
    setComponentModal = () => {},
    setData = () => {},
    dashboardName, // for testing random get api call and server is down
  }) => {
    const { isDark } = useSelector((state) => state.theme);
    const dispatch = useDispatch();
    // const { currentSite, currentSiteSelectedTiles } = useSelector((state) => state.dashboard);
    const { groupData = [], dashboardFilter, dateRange, refetch } = useSelector((state) => state.dashboardV2);

    const [dialChangeKey, setDialChangeKey] = useState(0);
    const [currBP, setCurrBP] = useState("lg");

    const onLayoutChange = (_, allLayouts) => setLayouts(allLayouts);
    const onDragStop = () => setDialChangeKey((prev) => prev + 1);

    const handleCopy = (item) => {
      Swal.fire({
        title: "Copy Tile?",
        text: "This will create a duplicate of this component.",
        showCancelButton: true,
        confirmButtonText: "Confirm",
      }).then((result) => {
        if (result.isConfirmed) {
          const _id = uuid();

          const sourceLayout = layouts[currBP]?.find((l) => String(l.i) === String(item._id));
          const width = sourceLayout?.w || 3;
          const height = sourceLayout?.h || 3;
          const nextPos = calculateNextPosition(layouts[currBP], width, height, GRID_CONFIG.cols[currBP]);

          setGridData((prev) => [...prev, { ...item, _id, chartTitle: `${item.chartTitle} (copy)` }]);

          setLayouts((prev) => ({
            ...prev,
            [currBP]: [...(prev[currBP] || []), { i: _id, w: width, h: height, ...nextPos }],
          }));
        }
      });
    };

    const handleRemove = (id) => {
      Swal.fire({
        title: "Are you sure?",
        text: "This tile will be permanently removed.",
        showCancelButton: true,
        confirmButtonText: "Delete",
        confirmButtonColor: "#d33",
      }).then((result) => {
        if (result.isConfirmed) {
          setGridData((prev) => prev.filter((d) => d._id !== id));
          setLayouts((prev) => ({
            ...prev,
            [currBP]: prev[currBP]?.filter((l) => String(l.i) !== String(id)),
          }));
        }
      });
    };

    const selectHandler = (selectId) => {
      const foundId = dashboardFilter.selectedTiles.find((id) => id === selectId);
      const selectedTiles = foundId ? dashboardFilter.selectedTiles.filter((id) => id !== selectId) : [...dashboardFilter.selectedTiles, selectId];
      const updatedFilter = { ...dashboardFilter, selectedTiles };
      dispatch(setDashboardFilter(updatedFilter));
    };

    const handleLinkedTab = (tabId) => {
      if (tabId) {
        setData((pre) => {
          const current = pre?.tabs?.find((item) => item.isSelected);
          dispatch(setPrevTabId(current?.tabId || null));
          const nextTabs = pre?.tabs?.map((item) => ({ ...item, isSelected: item.tabId === tabId }));
          return { ...pre, tabs: nextTabs };
        });
      }
    };

    return (
      <div className="w-100">
        <ResponsiveReactGridLayout
          className={`layout ${mode !== "create" && (isDark ? "bg-dark" : "bg-white")} ${mode === "view" ? "client_view" : "create"}`}
          rowHeight={80}
          layouts={layouts}
          onLayoutChange={onLayoutChange}
          onDragStop={onDragStop}
          onResizeStop={onDragStop}
          draggableCancel=".clickBtn"
          isDraggable={mode !== "view"}
          isResizable={mode !== "view"}
          breakpoints={GRID_CONFIG.breakpoints}
          cols={GRID_CONFIG.cols}
          onBreakpointChange={setCurrBP}
        >
          {gridData.map((tile) => {
            const Component = COMPONENT_MAP[tile.component];
            const { selectedTiles = [], comparisonPeriods = [], window, ...rest } = dashboardFilter || {};
            const isSelected = selectedTiles?.includes(tile._id);
            const cleanedRest = cleanObject(rest);

            // ---------- all filters are here ----------

            // Process specialized selection logic
            let displayData = { ...tile, locations: { ...tile.locations }, refetch };

            // for live edit
            const currentItemFromGroup = groupData?.find((group) => (group.ids || []).includes(String(displayData?._id)));
            if (currentItemFromGroup?.parameters?.length) displayData.locations.yAxis = currentItemFromGroup.parameters;

            // filter by date [quick select, absolute date]
            if (dateRange.startDate && dateRange.endDate) {
              displayData.timeRange = [dateRange.startDate, dateRange.endDate];
              displayData.xAxis = "custom";
            }

            // custom selector
            if (isSelected) {
              const locations = { ...displayData.locations, ...cleanedRest.locations };
              displayData = { ...displayData, ...cleanedRest, locations };
              if (comparisonPeriods?.length) {
                displayData.comparisonPeriods = comparisonPeriods;
                displayData.window = window;
              }
            }

            // for add new item default position and height
            const nextPos = calculateNextPosition(layouts[currBP], 3, 3, GRID_CONFIG.cols[currBP]);
            const currentItemLayout = layouts[currBP]?.find((l) => String(l.i) === String(tile._id));

            return (
              <div
                key={tile._id}
                className={`dashboardTiles ${tile.isBorderHidden ? "" : "withBorder"} ${isSelected ? "active" : ""}`}
                data-grid={currentItemLayout || { ...nextPos, h: 3, w: 3, i: tile._id }}
              >
                <div className="position-relative h-100 w-100 d-flex align-items-center">
                  {mode !== "view" && (
                    <div className="d-flex position-absolute" style={{ top: 10, right: 15, zIndex: 10 }}>
                      <BiCopy className="clickBtn text-secondary me-2 cursor-pointer" onClick={() => handleCopy(tile)} />
                      <FiEdit
                        className="clickBtn text-secondary me-2 cursor-pointer"
                        onClick={() => {
                          setEditItem(tile);
                          setComponentModal(true);
                        }}
                      />
                      <MdDeleteForever className="clickBtn cursor-pointer" onClick={() => handleRemove(tile._id)} />
                    </div>
                  )}

                  <div className="p-2 h-100 w-100" onDoubleClick={() => tile.component !== "textBox" && selectHandler(tile._id)}>
                    {Component ? (
                      <div className="relative h-100" onClick={() => handleLinkedTab(displayData?.linkTabId)}>
                        <Component
                          data={displayData}
                          type={tile.component}
                          dialChangeKey={dialChangeKey}
                          // mainDashboard={mainDashboard}
                          isSelected={isSelected}
                          dashboardName={dashboardName}
                        />
                        {displayData?.linkTabId ? (
                          <div style={{ position: "absolute", bottom: 2, right: 10, zIndex: 999, color: "#cac8c8" }}>
                            <FaExternalLinkAlt />
                          </div>
                        ) : (
                          ""
                        )}
                      </div>
                    ) : (
                      <div className="text-muted small">Component missing: {tile.component}</div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </ResponsiveReactGridLayout>
      </div>
    );
  },
);

DashboardLayout.displayName = "DashboardLayout";

DashboardLayout.propTypes = {
  gridData: PropTypes.array,
  setGridData: PropTypes.func.isRequired,
  setLayouts: PropTypes.func.isRequired,
  layouts: PropTypes.object,
  mode: PropTypes.string,
  setEditItem: PropTypes.func,
  setComponentModal: PropTypes.func,
  setData: PropTypes.func,
  mainDashboard: PropTypes.bool,
  dashboardName: PropTypes.string,
};

export default DashboardLayout;
